import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Loader2, Sparkles, Download, FileText, ExternalLink, CheckCircle2, AlertTriangle, Zap, Send, Mail, Eye, MailOpen, Presentation } from "lucide-react";
import { toast } from "sonner";
import { createAudit, listAudits, audit_download_url, getClients, sendAuditEmail, listAuditSends, API_BASE } from "../lib/api";

// API_BASE imported from ../lib/api (smart-resolves to window.location.origin in
// production so download <a> links go to the same domain the user logged in on).

function gradeColor(grade) {
  if (!grade) return "bg-zinc-100 text-zinc-700 border-zinc-200";
  const g = grade[0].toUpperCase();
  if (g === "A") return "bg-emerald-100 text-emerald-800 border-emerald-300";
  if (g === "B") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (g === "C") return "bg-amber-100 text-amber-800 border-amber-300";
  if (g === "D") return "bg-orange-100 text-orange-800 border-orange-300";
  return "bg-red-100 text-red-800 border-red-300";
}

function gradeBoxBg(grade) {
  // Big letter-grade square uses navy for A/B, red for C/D/F (matches sample)
  if (!grade) return "bg-slate-500";
  const g = grade[0].toUpperCase();
  if (g === "A" || g === "B") return "bg-[#0F2747]";
  return "bg-[#B91C1C]";
}

function categoryPillBg(category) {
  const c = (category || "").toUpperCase().trim();
  if (c === "SEO") return "bg-[#0F2747]";
  if (c === "AEO") return "bg-[#B91C1C]";
  return "bg-[#6B21A8]"; // SEO + AEO
}

function TopBar({ audit, domain, date }) {
  return (
    <div className="bg-[#0F2747] text-white px-5 py-3 flex items-start justify-between relative">
      <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-[#B91C1C]" />
      <div>
        <div className="text-[10px] font-bold tracking-wider text-[#FCA5A5]">SEO + AEO WEBSITE AUDIT</div>
        <div className="text-2xl font-bold mt-0.5">{audit.client_name}</div>
      </div>
      <div className="text-right shrink-0 ml-3">
        <div className="text-sm font-bold">{domain}</div>
        {audit.scope && <div className="text-[10px] text-[#94A3B8] mt-0.5">{audit.scope}</div>}
        <div className="text-[10px] text-[#94A3B8]">Audit date: {date}</div>
      </div>
    </div>
  );
}

export function AuditSlidePreview({ audit }) {
  if (!audit) return null;
  const domain = (audit.website_url || "").replace(/^https?:\/\//, "").replace(/\/$/, "");
  const date = new Date(audit.created_at || Date.now()).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  const strengths = (audit.strengths || []).slice(0, 5);
  const opportunities = (audit.opportunities || []).slice(0, 5);
  const quickWins = (audit.quick_wins || []).slice(0, 3);

  return (
    <div className="space-y-4" data-testid="audit-slide-preview">
      {/* ============ SLIDE 1 — Report Card + Recommendation ============ */}
      <div className="bg-white rounded-xl border-2 border-zinc-200 overflow-hidden shadow-md" style={{ aspectRatio: "16 / 9" }}>
        <TopBar audit={audit} domain={domain} date={date} />

        <div className="px-5 pt-3">
          <div className="text-[10px] font-bold tracking-wider text-[#475569]">YOUR REPORT CARD</div>
        </div>

        <div className="grid grid-cols-2 gap-4 px-5 pt-2">
          {[
            { accent: "#0F2747", label: "SEO Grade", sub: "Search Engine Optimization", grade: audit.seo_grade, score: audit.seo_score, summary: audit.seo_summary },
            { accent: "#B91C1C", label: "AEO Grade", sub: "Answer Engine Optimization", grade: audit.aeo_grade, score: audit.aeo_score, summary: audit.aeo_summary },
          ].map((g, i) => (
            <div key={i} className="bg-[#F8FAFC] flex gap-4 px-4 py-3" style={{ borderLeft: `5px solid ${g.accent}` }}>
              <div className="shrink-0 flex flex-col items-center">
                <div className={`${gradeBoxBg(g.grade)} w-24 h-24 flex items-center justify-center`}>
                  <span className="text-white font-bold text-5xl" style={{ fontFamily: "Georgia, serif" }}>{g.grade}</span>
                </div>
                <div className={`${gradeBoxBg(g.grade)} text-white text-sm font-bold px-3 py-1 mt-1 w-24 text-center`}>{g.score} / 100</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xl font-bold text-[#0F172A]">{g.label}</div>
                <div className="text-[10px] text-[#475569] uppercase tracking-wider mt-0.5">{g.sub}</div>
                <p className="text-[13px] text-[#0F172A] mt-2 leading-relaxed">{g.summary}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-[#0F2747] text-white text-xs font-bold tracking-wider px-5 py-2 mt-3 mx-5">OUR RECOMMENDATION</div>
        <p className="text-base text-[#0F172A] leading-relaxed mx-5 mt-3 px-1">{audit.recommendation}</p>
        <p className="text-base font-bold text-[#0F2747] mx-5 mt-4 px-1">See next slide for AEO/SEO strengths and weaknesses.</p>
      </div>

      {/* ============ SLIDE 2 — Strengths + Opportunities + Quick Wins ============ */}
      <div className="bg-white rounded-xl border-2 border-zinc-200 overflow-hidden shadow-md" style={{ aspectRatio: "16 / 9" }}>
        <TopBar audit={audit} domain={domain} date={date} />

        <div className="grid grid-cols-2 gap-4 px-5 pt-3">
          <div>
            <div className="bg-[#DCFCE7] flex items-center justify-between px-3 py-2">
              <span className="text-[#15803D] font-bold text-sm">WHAT&apos;S WORKING WELL</span>
              <span className="text-[#15803D] font-bold text-[9px] tracking-wider">STRENGTHS</span>
            </div>
            <ul className="mt-3 space-y-3">
              {strengths.map((s, i) => (
                <li key={i} className="flex gap-2">
                  <span className="bg-[#15803D] text-white text-[11px] font-bold w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">✓</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-[12px] font-bold text-[#0F172A] leading-tight">{s.title}</div>
                      <span className={`${categoryPillBg(s.category)} text-white text-[8px] font-bold px-1.5 py-0.5 shrink-0 tracking-wider`}>{s.category}</span>
                    </div>
                    <div className="text-[11px] text-[#475569] mt-1 leading-snug">{s.detail}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="bg-[#FEF3C7] flex items-center justify-between px-3 py-2">
              <span className="text-[#B45309] font-bold text-sm">WHERE TO IMPROVE</span>
              <span className="text-[#B45309] font-bold text-[9px] tracking-wider">OPPORTUNITIES</span>
            </div>
            <ul className="mt-3 space-y-3">
              {opportunities.map((o, i) => (
                <li key={i} className="flex gap-2">
                  <span className="bg-[#B45309] text-white text-[11px] font-bold w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">!</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-[12px] font-bold text-[#0F172A] leading-tight">{o.title}</div>
                      <span className={`${categoryPillBg(o.category)} text-white text-[8px] font-bold px-1.5 py-0.5 shrink-0 tracking-wider`}>{o.category}</span>
                    </div>
                    <div className="text-[11px] text-[#475569] mt-1 leading-snug">{o.detail}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="bg-[#081A33] text-white mt-4 px-5 py-4 grid gap-4" style={{ gridTemplateColumns: "auto 1fr 1fr 1fr" }}>
          <div className="text-[#FCA5A5] font-bold text-xs tracking-wider self-center pr-3 border-r border-[#1E3A5F] leading-snug">TOP 3 QUICK WINS<br/>TO MOVE UP A<br/>GRADE</div>
          {quickWins.map((w, i) => (
            <div key={i} className="flex gap-2 items-start">
              <span className="text-[#B91C1C] font-bold text-4xl leading-none" style={{ fontFamily: "Georgia, serif" }}>{i + 1}</span>
              <div className="min-w-0">
                <div className="text-white font-bold text-sm leading-tight">{w.title}</div>
                <div className="text-[#CBD5E1] text-xs mt-1 leading-snug">{w.detail}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function EmailAuditDialog({ audit, trigger }) {
  const [open, setOpen] = useState(false);
  const [toEmail, setToEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const defaultSubject = audit ? `Your SEO + AEO audit — ${audit.client_name}` : "";
  const defaultBody = (() => {
    if (!audit) return "";
    const domain = (audit.website_url || "").replace(/^https?:\/\//, "").replace(/\/$/, "");
    return `Hi there,\n\nHere's the instant SEO + AEO audit I mentioned for ${domain} — your SEO grade is ${audit.seo_grade} and AEO grade is ${audit.aeo_grade}. The "Top 3 Quick Wins" on page 2 are the fastest path to move up a grade.\n\nHappy to walk you through it.`;
  })();

  // Reset whenever opened
  useEffect(() => {
    if (open) {
      setSubject(defaultSubject);
      setBody(defaultBody);
      setToEmail("");
    }
  }, [open, audit?.id]);

  const handleSend = async () => {
    if (!toEmail.trim() || !/.+@.+\..+/.test(toEmail.trim())) {
      toast.error("Enter a valid recipient email");
      return;
    }
    setSending(true);
    try {
      await sendAuditEmail(audit.id, {
        to_email: toEmail.trim(),
        subject: subject.trim() || defaultSubject,
        body: body.trim() || defaultBody,
      });
      toast.success(`Audit sent to ${toEmail.trim()}`);
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button size="sm" data-testid="audit-email-trigger">
            <Send className="mr-1 h-4 w-4" /> Email to Prospect
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-indigo-600" /> Email Audit to Prospect
          </DialogTitle>
          <DialogDescription>
            The PDF will be attached. Replies go straight to your inbox, and we&apos;ll show you when the prospect opens the email or views the audit online.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="audit-send-to" className="text-xs text-muted-foreground">Recipient email *</Label>
            <Input
              id="audit-send-to"
              type="email"
              value={toEmail}
              onChange={(e) => setToEmail(e.target.value)}
              placeholder="prospect@theirdomain.com"
              data-testid="audit-send-to-input"
            />
          </div>
          <div>
            <Label htmlFor="audit-send-subject" className="text-xs text-muted-foreground">Subject</Label>
            <Input
              id="audit-send-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={defaultSubject}
              data-testid="audit-send-subject-input"
            />
          </div>
          <div>
            <Label htmlFor="audit-send-body" className="text-xs text-muted-foreground">Cover note</Label>
            <Textarea
              id="audit-send-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={8}
              className="font-sans"
              data-testid="audit-send-body-input"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              We&apos;ll append a &ldquo;View online&rdquo; link with open tracking + your sign-off automatically.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSend} disabled={sending || !toEmail.trim()} data-testid="audit-send-confirm-btn">
            {sending ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...</>
            ) : (
              <><Send className="mr-2 h-4 w-4" /> Send Audit</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export function AuditBuilder({ proposalId, onAuditGenerated, prefill = {} }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("new"); // new | existing
  const [clients, setClients] = useState([]);
  const [websiteUrl, setWebsiteUrl] = useState(prefill.website_url || "");
  const [clientName, setClientName] = useState(prefill.client_name || "");
  const [scope, setScope] = useState(prefill.scope || "");
  const [selectedClientId, setSelectedClientId] = useState("");
  const [generating, setGenerating] = useState(false);
  const [audit, setAudit] = useState(null);

  useEffect(() => {
    if (open && mode === "existing" && clients.length === 0) {
      getClients().then((r) => setClients(r.data || [])).catch(() => setClients([]));
    }
  }, [open, mode, clients.length]);

  // When user picks an existing client, prefill the inputs
  useEffect(() => {
    if (selectedClientId) {
      const c = clients.find((x) => x.id === selectedClientId);
      if (c) {
        setClientName(c.company || c.name || "");
        setWebsiteUrl(c.website || "");
        setScope(c.industry || "");
      }
    }
  }, [selectedClientId, clients]);

  const handleGenerate = async () => {
    if (!websiteUrl.trim() || !clientName.trim()) {
      toast.error("Website URL and Client Name are required");
      return;
    }
    setGenerating(true);
    setAudit(null);
    try {
      const res = await createAudit({
        website_url: websiteUrl.trim(),
        client_name: clientName.trim(),
        scope: scope.trim() || null,
        client_id: mode === "existing" ? selectedClientId || null : null,
        proposal_id: proposalId || null,
      });
      setAudit(res.data);
      toast.success("Audit ready!");
      if (onAuditGenerated) onAuditGenerated(res.data);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Audit failed";
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const reset = () => {
    setAudit(null);
    setWebsiteUrl(prefill.website_url || "");
    setClientName(prefill.client_name || "");
    setScope(prefill.scope || "");
    setSelectedClientId("");
    setMode("new");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button
          size="lg"
          className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-md"
          data-testid="audit-builder-trigger"
        >
          <Sparkles className="mr-2 h-5 w-5" />
          Get an Instant Audit
        </Button>
      </DialogTrigger>

      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-indigo-600" />
            Get an Instant AEO/SEO Audit
          </DialogTitle>
          <DialogDescription>
            Claude Sonnet 4.5 analyzes the client&apos;s website and produces a one-page AEO + SEO audit with grades, strengths, opportunities, and quick wins.
          </DialogDescription>
        </DialogHeader>

        {!audit && (
          <div className="space-y-5">
            {/* Mode toggle */}
            <div className="flex gap-2 border-b pb-2">
              <Button
                type="button"
                variant={mode === "new" ? "default" : "outline"}
                size="sm"
                onClick={() => setMode("new")}
                data-testid="audit-mode-new"
              >
                New Client
              </Button>
              <Button
                type="button"
                variant={mode === "existing" ? "default" : "outline"}
                size="sm"
                onClick={() => setMode("existing")}
                data-testid="audit-mode-existing"
              >
                Existing Client
              </Button>
            </div>

            {mode === "existing" && (
              <div>
                <Label className="text-xs text-muted-foreground">Pick a client</Label>
                <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                  <SelectTrigger data-testid="audit-existing-client-select">
                    <SelectValue placeholder="Select an existing client..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.company || c.name} {c.website ? `· ${c.website}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="audit-client-name" className="text-xs text-muted-foreground">Client Name *</Label>
                <Input
                  id="audit-client-name"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="e.g., The Fellowship Family"
                  data-testid="audit-client-name-input"
                />
              </div>
              <div>
                <Label htmlFor="audit-url" className="text-xs text-muted-foreground">Website URL *</Label>
                <Input
                  id="audit-url"
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  placeholder="https://example.com"
                  data-testid="audit-url-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="audit-scope" className="text-xs text-muted-foreground">Category / Scope (optional)</Label>
              <Input
                id="audit-scope"
                value={scope}
                onChange={(e) => setScope(e.target.value)}
                placeholder="e.g., Senior Living · 11 communities · FL, GA, SC"
                data-testid="audit-scope-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Helps Claude evaluate YMYL signals and competitive context.
              </p>
            </div>

            <div className="bg-zinc-50 border rounded-md px-3 py-2 text-xs text-muted-foreground">
              Claude Sonnet 4.5 will fetch the homepage + key internal pages, analyze SEO + AEO signals, and produce a one-page audit. Expect <b>~30 seconds</b>.
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button
                onClick={handleGenerate}
                disabled={generating || !websiteUrl.trim() || !clientName.trim()}
                className="bg-gradient-to-r from-indigo-600 to-purple-600"
                data-testid="audit-generate-btn"
              >
                {generating ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Analyzing site...</>
                ) : (
                  <><Sparkles className="mr-2 h-4 w-4" /> Generate Audit</>
                )}
              </Button>
            </div>
          </div>
        )}

        {audit && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-sm text-emerald-700">
                <CheckCircle2 className="h-4 w-4" /> Audit complete · saved to your library
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button asChild variant="outline" size="sm" data-testid="audit-download-pdf">
                  <a href={`${API_BASE}/api/audits/${audit.id}/download?format=pdf`} download>
                    <FileText className="mr-1 h-4 w-4" /> Download PDF
                  </a>
                </Button>
                <Button asChild variant="outline" size="sm" data-testid="audit-download-pptx">
                  <a href={`${API_BASE}/api/audits/${audit.id}/download?format=pptx`} download>
                    <Download className="mr-1 h-4 w-4" /> 2-Slide PPTX
                  </a>
                </Button>
                <Button asChild size="sm" className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700" data-testid="audit-download-branded">
                  <a href={`${API_BASE}/api/audits/${audit.id}/download?format=pptx_branded`} download>
                    <Presentation className="mr-1 h-4 w-4" /> Full Sales Deck
                  </a>
                </Button>
                <EmailAuditDialog audit={audit} />
                <Button variant="ghost" size="sm" onClick={reset}>Run Another</Button>
              </div>
            </div>

            <AuditSlidePreview audit={audit} />

            {audit.fetched_urls?.length > 0 && (
              <div className="text-[11px] text-muted-foreground">
                Pages analyzed: {audit.fetched_urls.map((u, i) => (
                  <a key={i} href={u} target="_blank" rel="noreferrer" className="ml-1 underline hover:text-indigo-600">
                    {u.replace(/^https?:\/\//, "")} <ExternalLink className="inline h-3 w-3" />
                  </a>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function AuditHistoryList() {
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    listAudits().then((r) => setAudits(r.data || [])).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  if (audits.length === 0) {
    return <p className="text-sm text-muted-foreground py-2">No audits yet — click &ldquo;Get an Instant Audit&rdquo; above to generate your first one.</p>;
  }

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {audits.slice(0, 12).map((a) => (
          <Card
            key={a.id}
            className="cursor-pointer hover:border-indigo-300 transition"
            onClick={() => setViewing(a)}
            data-testid={`audit-card-${a.id}`}
          >
            <CardContent className="pt-4 pb-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold text-sm truncate">{a.client_name}</div>
                <div className="flex gap-1 shrink-0">
                  <Badge className={`text-[10px] ${gradeColor(a.seo_grade)}`}>SEO {a.seo_grade}</Badge>
                  <Badge className={`text-[10px] ${gradeColor(a.aeo_grade)}`}>AEO {a.aeo_grade}</Badge>
                </div>
              </div>
              <div className="text-[11px] text-muted-foreground truncate">{a.website_url}</div>
              <div className="text-[10px] text-muted-foreground">
                {a.generated_by_name} · {new Date(a.created_at).toLocaleDateString()}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{viewing?.client_name}</DialogTitle>
            <DialogDescription>
              {viewing?.website_url} · Audit generated {viewing && new Date(viewing.created_at).toLocaleDateString()}
            </DialogDescription>
          </DialogHeader>
          {viewing && (
            <div className="space-y-3">
              <div className="flex gap-2 flex-wrap">
                <Button asChild variant="outline" size="sm">
                  <a href={`${API_BASE}/api/audits/${viewing.id}/download?format=pdf`} download>
                    <FileText className="mr-1 h-4 w-4" /> PDF
                  </a>
                </Button>
                <Button asChild variant="outline" size="sm">
                  <a href={`${API_BASE}/api/audits/${viewing.id}/download?format=pptx`} download>
                    <Download className="mr-1 h-4 w-4" /> 2-Slide PPTX
                  </a>
                </Button>
                <Button asChild size="sm" className="bg-gradient-to-r from-indigo-600 to-purple-600">
                  <a href={`${API_BASE}/api/audits/${viewing.id}/download?format=pptx_branded`} download>
                    <Presentation className="mr-1 h-4 w-4" /> Full Sales Deck
                  </a>
                </Button>
                <EmailAuditDialog audit={viewing} />
              </div>
              <AuditSlidePreview audit={viewing} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}


function relativeTime(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function AuditSendsList() {
  const [sends, setSends] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = () => listAuditSends()
      .then((r) => { if (alive) setSends(r.data || []); })
      .finally(() => { if (alive) setLoading(false); });
    load();
    // Re-poll every 30s so opens/views update without a refresh
    const t = setInterval(load, 30000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  if (sends.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-2" data-testid="audit-sends-empty">
        No audits sent yet — click <b>Email to Prospect</b> on any audit to send your first one. We&apos;ll track opens and online views here.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto border rounded-md" data-testid="audit-sends-list">
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 text-zinc-600 text-[11px] uppercase tracking-wider">
          <tr>
            <th className="text-left px-3 py-2 font-semibold">Client</th>
            <th className="text-left px-3 py-2 font-semibold">Recipient</th>
            <th className="text-left px-3 py-2 font-semibold">Sent</th>
            <th className="text-left px-3 py-2 font-semibold">Email Opened</th>
            <th className="text-left px-3 py-2 font-semibold">PDF Viewed</th>
            <th className="text-left px-3 py-2 font-semibold">Sent by</th>
          </tr>
        </thead>
        <tbody>
          {sends.map((s) => (
            <tr key={s.id} className="border-t hover:bg-zinc-50" data-testid={`audit-send-row-${s.id}`}>
              <td className="px-3 py-2">
                <div className="font-semibold text-zinc-900 truncate max-w-[200px]">{s.audit_client_name}</div>
                <div className="text-[11px] text-muted-foreground truncate max-w-[200px]">{(s.audit_website || "").replace(/^https?:\/\//, "")}</div>
              </td>
              <td className="px-3 py-2 text-zinc-700 truncate max-w-[220px]" title={s.to_email}>{s.to_email}</td>
              <td className="px-3 py-2 text-zinc-600 whitespace-nowrap" title={s.sent_at}>{relativeTime(s.sent_at)}</td>
              <td className="px-3 py-2 whitespace-nowrap">
                {s.opened_at ? (
                  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-100">
                    <MailOpen className="h-3 w-3 mr-1" /> Opened · {relativeTime(s.opened_at)}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-muted-foreground">Not opened</Badge>
                )}
              </td>
              <td className="px-3 py-2 whitespace-nowrap">
                {s.viewed_at ? (
                  <Badge className="bg-indigo-100 text-indigo-800 border-indigo-300 hover:bg-indigo-100">
                    <Eye className="h-3 w-3 mr-1" /> Viewed{s.view_count > 1 ? ` ${s.view_count}x` : ""} · {relativeTime(s.viewed_at)}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-muted-foreground">Not viewed</Badge>
                )}
              </td>
              <td className="px-3 py-2 text-zinc-600 text-[12px] whitespace-nowrap">{s.sent_by_name}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
